/**
 * Site Health → Google Business Profile.
 *
 * Shows what the team actually asked for from Local Falcon (standup
 * 2026-08-25): the RECOMMENDATIONS, each with the reason it matters, and the
 * ALREADY COMPLETE list. Heat maps were explicitly declined.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	AlertTriangle,
	CheckCircle2,
	ExternalLink,
	Inbox,
	Loader2,
	MapPin,
	Search,
	Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import QuickScanDialog from "@/components/GoogleBusinessProfile/QuickScanDialog";
import GbpAuditPanel from "@/components/GoogleBusinessProfile/GbpAuditPanel";
import GbpAuditHistory from "@/components/GoogleBusinessProfile/GbpAuditHistory";
import GbpScanHistory from "@/components/GoogleBusinessProfile/GbpScanHistory";
import GbpReviewsPanel from "@/components/GoogleBusinessProfile/GbpReviewsPanel";
import {
	gbpAuditService,
	type GbpAuditResult,
} from "@/services/gbpAuditService";
import GbpWaitState from "@/components/GoogleBusinessProfile/GbpWaitState";
import { useElapsedSeconds } from "@/components/GoogleBusinessProfile/useElapsedSeconds";
import { gbpListingLoadingStage } from "@/components/GoogleBusinessProfile/gbpAuditLoadingCopy";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@/components/ui/tabs";
import { useTenantStore } from "@/stores/useTenantStore";
import {
	gbpRecommendationsService,
	type GbpRecommendation,
	type QuickScanParams,
	type QuickScanRun,
} from "@/services/gbpRecommendationsService";

/** Valid tab ids. An unknown ?tab= falls back rather than rendering blank. */
const TABS = ["visibility", "audit", "reviews", "history"];

/** Matches the 10s cadence the rest of the audit surfaces poll at. */
const SCAN_POLL_MS = 10_000;

/**
 * Every outcome that means "stop polling". Omitting "reused" would leave the
 * page spinning forever on a scan that already finished. "dry_run" was one of
 * these until the backend's dry-run switch was removed (2026-09-02); it is
 * still tolerated so a backend that has not yet been redeployed cannot leave
 * this page polling for ever.
 */
const isTerminal = (status: string) =>
	status === "complete" ||
	status === "failed" ||
	status === "reused" ||
	status === "dry_run";

function RecommendationRow({ item }: { item: GbpRecommendation }) {
	const isMajor = item.severity === "major";
	return (
		<li className="rounded-lg border border-border bg-card p-4">
			<div className="flex items-start gap-3">
				<AlertTriangle
					className={`mt-0.5 h-4 w-4 shrink-0 ${
						isMajor ? "text-coral" : "text-teal-dark"
					}`}
				/>
				<div className="min-w-0">
					<div className="flex flex-wrap items-center gap-2">
						<span className="font-medium text-foreground">{item.title}</span>
						<span
							className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${
								isMajor
									? "border-coral/40 bg-coral-light text-coral-dark"
									: "border-teal/40 bg-teal-light text-teal-dark"
							}`}
						>
							{isMajor ? "major" : "minor"}
						</span>
					</div>
					{/* The "why" is the reason this feature exists — always show it. */}
					{item.reason && (
						<p className="mt-1 text-sm text-muted-foreground">{item.reason}</p>
					)}
				</div>
			</div>
		</li>
	);
}

export default function GbpRecommendationsPage() {
	const { currentTenant } = useTenantStore();
	const tenantId = currentTenant || "";
	const queryClient = useQueryClient();

	// Which branch is on screen. "" means "whatever the server calls primary".
	const [branch, setBranch] = useState("");
	const [scanOpen, setScanOpen] = useState(false);
	const [activeRun, setActiveRun] = useState<QuickScanRun | null>(null);
	const [scanError, setScanError] = useState<string | null>(null);
	const [scanNote, setScanNote] = useState("");
	// What the in-flight scan was STARTED with — the status endpoint needs it to
	// know when to stop waiting for a written analysis.
	const [runWantsAi, setRunWantsAi] = useState(true);

	// The tab lives in the URL, unlike CompetitorComparisonPage's plain useState.
	// An audit finding gets pasted into Slack and a client report, and a link
	// that always lands on Search Visibility makes the reader hunt for what was
	// being pointed at. `replace` keeps the back button meaning "leave this
	// page" rather than "undo three tab clicks".
	const [searchParams, setSearchParams] = useSearchParams();
	const requestedTab = searchParams.get("tab") ?? "";
	const tab = TABS.includes(requestedTab) ? requestedTab : "visibility";
	const setTab = (next: string) => {
		const params = new URLSearchParams(searchParams);
		params.set("tab", next);
		setSearchParams(params, { replace: true });
	};

	// Which scan the Search Visibility tab is showing. Empty = the latest,
	// which is the ordinary case. It lives in the URL for the same reason the
	// tab does: "look at this scan" is a thing people paste to each other.
	const selectedReport = searchParams.get("report") ?? "";

	const viewScan = (reportKey: string) => {
		// PUSH, not replace. Opening a specific scan is a deliberate move to a
		// new view, and Back should return to the history list you came from.
		// Everything else on this page replaces, so ordinary tab clicks still
		// do not pile up entries — but with replace here too, the whole visit
		// collapsed into ONE entry and Back left the page entirely, landing
		// the user on Overview (reported 2026-09-15).
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("report", reportKey);
			next.set("tab", "visibility");
			return next;
		});
	};

	// Which stored AUDIT is being re-opened. Separate from ?report= (a
	// visibility scan) because the two histories are different things.
	const selectedAudit = searchParams.get("audit") ?? "";

	const viewAudit = (runId: string) => {
		// PUSH — same reasoning as viewScan.
		setSearchParams((prev) => {
			const next = new URLSearchParams(prev);
			next.set("audit", runId);
			next.set("tab", "audit");
			return next;
		});
	};

	// Which audit topic the left rail has open. In the URL for the same reason
	// the tab is: "look at Photos on this client" should be a link you can
	// paste, not a click someone has to be talked through.
	const selectedSection = searchParams.get("section") ?? "";

	const setSection = (next: string) => {
		setSearchParams(
			(prev) => {
				const params = new URLSearchParams(prev);
				params.set("section", next);
				return params;
			},
			{ replace: true },
		);
	};

	const backToLatestAudit = () => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("audit");
				return next;
			},
			{ replace: true },
		);
	};

	const backToLatest = () => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("report");
				return next;
			},
			{ replace: true },
		);
	};

	const { data, isLoading, isError } = useQuery({
		queryKey: [
			"site-health",
			"gbp-recommendations",
			tenantId,
			branch,
			selectedReport,
		],
		queryFn: () =>
			gbpRecommendationsService.get(tenantId, branch, selectedReport),
		enabled: Boolean(tenantId),
	});

	// Switching client must not carry the previous client's branch across — a
	// stale place_id would be refused server-side, but the picker would still
	// look wrong for a moment.
	useEffect(() => {
		setBranch("");
	}, [tenantId]);

	// Stays `replace`: this fires from an effect, not a click. Pushing from an
	// effect inserts an entry the user never asked for, so one Back press
	// would look like a no-op and a second would be needed to leave.
	//
	// A report belongs to ONE listing, so a selection cannot survive a change
	// of client or branch. The server would refuse it anyway; clearing it here
	// means the operator sees the latest scan instead of an error they did
	// nothing to cause.
	useEffect(() => {
		if (!selectedAudit) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("audit");
				return next;
			},
			{ replace: true },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenantId, branch]);

	useEffect(() => {
		if (!selectedReport) return;
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("report");
				return next;
			},
			{ replace: true },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tenantId, branch]);

	// A scan is started ONLY here, from an explicit confirmed click — never from
	// a render, a refetch, or a retry, because each one spends credits.
	const startScan = useMutation({
		mutationFn: (params: QuickScanParams) =>
			gbpRecommendationsService.startQuickScan(params),
		onSuccess: (run) => {
			setActiveRun(run);
			setScanError(null);
			setScanOpen(false);
			setScanNote(run.detail || "");
			// A reused scan already has its answer waiting; pull it in rather
			// than leaving the page on stale data.
			if (run.status === "reused") {
				queryClient.invalidateQueries({
					queryKey: ["site-health", "gbp-recommendations", tenantId],
				});
			}
		},
		onError: async (err: unknown) => {
			const response = (err as { response?: { data?: { detail?: string } } })
				?.response;
			const message =
				response?.data?.detail ||
				(err as Error)?.message ||
				"Couldn't start the scan.";

			// No response at all means the request died in transit — NOT that
			// the scan did. On 2026-09-03 exactly this spent 34 credits, ran to
			// completion server-side, and told the operator "Network Error".
			// Look for the scan before claiming anything failed. The lookup is
			// free and cannot start a second one.
			if (!response) {
				try {
					const found = await gbpRecommendationsService.findInFlightScan(
						tenantId,
						branch,
					);
					if (found) {
						setActiveRun(found);
						setScanError(null);
						setScanOpen(false);
						setScanNote(
							found.detail ||
								"That scan is running — the browser lost the reply, not the scan.",
						);
						return;
					}
				} catch {
					// Fall through to the plain error: a failed recovery attempt
					// must not replace the message the operator needs to see.
				}
			}
			setScanError(message);
		},
		// No retry: a retried POST could start a second paid scan.
		retry: false,
	});

	// A scan started before this page loaded — or before the last reload — is
	// still the operator's scan. Without this, the only handle on it (the report
	// key) lives in a response that may never have arrived. Free to ask.
	useEffect(() => {
		if (activeRun) return;
		let cancelled = false;
		Promise.resolve(
			gbpRecommendationsService.findInFlightScan(tenantId, branch),
		)
			.then((found) => {
				if (!cancelled && found) {
					setActiveRun(found);
					setScanNote(found.detail || "");
				}
			})
			.catch(() => {
				// Nothing to recover, or the lookup failed. Either way the page
				// works exactly as it did before.
			});
		return () => {
			cancelled = true;
		};
	}, [tenantId, branch, activeRun]);

	const runId = activeRun?.id ?? "";
	const runInFlight = Boolean(runId) && !isTerminal(activeRun?.status ?? "");

	const { data: runStatus } = useQuery({
		queryKey: ["site-health", "gbp-quick-scan", tenantId, runId, runWantsAi],
		// The poll has to know whether an analysis is coming: the backend waits
		// for one before saying "complete", so asking as though a no-analysis
		// scan had one would poll for ever.
		queryFn: () =>
			gbpRecommendationsService.getScanStatus(tenantId, runId, runWantsAi),
		enabled: runInFlight,
		refetchInterval: SCAN_POLL_MS,
	});

	useEffect(() => {
		if (!runStatus) return;
		setActiveRun(runStatus);
		if (runStatus.status === "complete") {
			// The fresh report is what the operator asked for — pull it in.
			queryClient.invalidateQueries({
				queryKey: ["site-health", "gbp-recommendations", tenantId],
			});
		} else if (runStatus.status === "failed") {
			setScanError(runStatus.error || "The scan failed.");
		}
	}, [runStatus, queryClient, tenantId]);

	/* The unanswered count for the Reviews tab label.
	 *
	 * Deliberately the SAME query key the reviews panel uses, so this shares
	 * one cache entry and one request -- it reads the audit, it does not
	 * trigger a second one. null until we know, because a "(0)" shown while
	 * the audit is still reading would say "nothing to do" when the truth is
	 * "we have not looked yet". Mike Hammer Moving genuinely has 375.
	 */
	const { data: auditForCount } = useQuery<GbpAuditResult>({
		queryKey: ["site-health", "gbp-audit", tenantId, branch, selectedAudit],
		queryFn: () => gbpAuditService.get(tenantId, branch, false, selectedAudit),
		enabled: Boolean(tenantId),
	});
	const unansweredCount = ((): number | null => {
		const raw = auditForCount?.reviewSummary?.unanswered;
		const n = typeof raw === "number" ? raw : Number(raw);
		return Number.isFinite(n) ? n : null;
	})();

	const listingElapsed = useElapsedSeconds(isLoading);

	const location = data?.location ?? null;
	const branches = data?.locations ?? [];
	const canScan = Boolean(location?.canScan);

	if (isLoading) {
		/* Was a bare <Spin>. It replaced the WHOLE page, so the audit panel
		   below never mounted and its own staged copy never got a chance --
		   switching client still looked like a hung page even after that copy
		   landed (2026-09-23). Same treatment as the audit wait, so the two
		   back-to-back waits do not visibly swap mid-flight. */
		return (
			<GbpWaitState
				stage={gbpListingLoadingStage(listingElapsed)}
				testId="gbp-listing-loading"
			/>
		);
	}

	if (isError) {
		return (
			<div className="p-6 text-sm text-destructive">
				Couldn&apos;t load Google Business Profile recommendations.
			</div>
		);
	}

	// Never connected is a different state from "nothing to fix".
	if (data && !data.connected) {
		return (
			<div className="p-6">
				<div className="rounded-lg border border-coral/40 bg-coral-light p-4">
					<p className="font-medium text-coral-dark">
						Profile scanning isn&apos;t connected for this client
					</p>
					<p className="mt-1 text-sm text-coral-dark">
						Google Business Profile scanning isn&apos;t set up for this client
						yet, so there is no data to show. This is not
						a clean bill of health.
					</p>
				</div>
			</div>
		);
	}

	// Connected but this client has no Local Falcon location assigned. Distinct
	// from "not connected" and emphatically not "nothing to fix".
	if (data && data.connected && !data.mapped) {
		return (
			<div className="p-6" data-testid="gbp-not-mapped">
				<div className="rounded-lg border border-coral/40 bg-coral-light p-4">
					<p className="font-medium text-coral-dark">
						No Google Business Profile location is mapped to this client
					</p>
					<p className="mt-1 text-sm text-coral-dark">
						Profile scanning is connected, but nothing links {tenantId} to a
						business location yet, so there is no report to read. Map the
						location to enable recommendations.
					</p>
				</div>
			</div>
		);
	}

	const recommendations = data?.recommendations ?? [];
	const completed = data?.completed ?? [];
	const citations = data?.citations ?? [];
	const vulnerable = data?.vulnerable ?? [];

	return (
		<div className="space-y-6 p-6" data-testid="gbp-recommendations-page">
			<header className="flex flex-wrap items-start justify-between gap-3">
				<div>
					<h1 className="flex items-center gap-2 text-xl font-semibold text-foreground">
						<MapPin className="h-5 w-5 text-coral" />
						Google Business Profile
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						{location?.name ? `${location.name} · ` : ""}
						{data?.keyword
							? `Latest profile scan for “${data.keyword}”`
							: "Latest profile scan"}
						{data?.scannedAt ? ` · ${data.scannedAt}` : ""}
					</p>
				</div>
				<Button
					onClick={() => {
						setScanError(null);
						setScanOpen(true);
					}}
					disabled={!canScan || runInFlight}
					data-testid="quick-scan-open"
				>
					{runInFlight ? (
						<Loader2 className="mr-2 h-4 w-4 animate-spin" />
					) : (
						<Search className="mr-2 h-4 w-4" />
					)}
					{runInFlight ? "Scanning…" : "Quick Scan"}
				</Button>
			</header>

			{/* Several offices for one client (Edelman-Lyon has four, Prestige ER
			    three, each targeting a different local search term). Showing one
			    without saying which would read as company-wide. */}
			{branches.length > 1 && (
				<div
					className="flex flex-wrap items-center gap-2"
					data-testid="gbp-branch-picker"
				>
					<span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
						Location
					</span>
					{branches.map((item) => {
						const active = item.placeId === location?.placeId;
						return (
							<Button
								key={item.placeId}
								size="sm"
								variant={active ? "default" : "outline"}
								aria-pressed={active}
								onClick={() => setBranch(item.placeId)}
								data-testid={`gbp-branch-${item.placeId}`}
							>
								{item.name || "Unnamed location"}
							</Button>
						);
					})}
				</div>
			)}

			{runInFlight && (
				<div
					className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground"
					data-testid="quick-scan-progress"
				>
					Scanning “{activeRun?.keyword}”. This usually takes a couple of
					minutes — the recommendations below refresh automatically when it
					finishes.
				</div>
			)}

			{scanNote && !scanOpen && (
				<div
					className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground"
					data-testid="quick-scan-note"
				>
					{scanNote}
				</div>
			)}

			{scanError && !scanOpen && (
				<div
					className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive"
					data-testid="quick-scan-page-error"
				>
					{scanError}
				</div>
			)}

			<QuickScanDialog
				open={scanOpen}
				onOpenChange={setScanOpen}
				tenantId={tenantId}
				locationName={location?.name ?? ""}
				canScan={canScan}
				businessLat={location?.lat}
				businessLng={location?.lng}
				onConfirm={(params) => {
					setRunWantsAi(params.aiAnalysis !== false);
					startScan.mutate(params);
				}}
				isSubmitting={startScan.isPending}
				error={scanOpen ? scanError : null}
			/>

			<Tabs value={tab} onValueChange={setTab} className="w-full">
				<TabsList className="grid w-full grid-cols-4 mb-4">
					<TabsTrigger value="visibility">Search Visibility</TabsTrigger>
					<TabsTrigger value="audit">Profile Audit</TabsTrigger>
					<TabsTrigger value="reviews">
						Google Reviews
						{/* Only shown once we know it. A count of zero while the
						    audit is still reading would read as "nothing to do". */}
						{unansweredCount !== null && ` (${unansweredCount})`}
					</TabsTrigger>
					{/* The URL values stay "reviews" and "history": they are in
					    links people have already shared, and a label is not a
					    reason to break one. */}
					<TabsTrigger value="history">Scan History</TabsTrigger>
				</TabsList>

				<TabsContent value="visibility" className="space-y-6">
					{selectedReport && (
						<div
							className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-coral/40 bg-coral-light p-3 text-sm"
							data-testid="gbp-viewing-past-scan"
						>
							<span className="text-coral-dark">
								Showing a past scan
								{data?.keyword ? ` for “${data.keyword}”` : ""}
								{data?.scannedAt ? ` from ${data.scannedAt}` : ""}. This is
								not the current picture.
							</span>
							<Button variant="outline" size="sm" onClick={backToLatest}>
								Back to the latest scan
							</Button>
						</div>
					)}

				{data?.summary && (
					<p className="rounded-lg border border-border bg-card p-4 text-sm text-foreground">
						{data.summary}
					</p>
				)}

				{/* Neither must a scan that was never asked to produce one. Checked
				    first: with no analysis there is nothing to be unreadable. */}
				{data?.analysisMissing && !data?.unreadable && (
					<div
						className="rounded-lg border border-coral/40 bg-coral-light p-4 text-sm text-coral-dark"
						data-testid="gbp-analysis-missing"
					>
						The latest scan was run without the AI analysis, so there are no
						recommendations to show — this is “not asked”, not “nothing to fix”.
						Run a new scan with the analysis switched on to get them.
					</div>
				)}

				{/* An unreadable analysis must never look like a clean result. */}
				{data?.unreadable && (
					<div className="rounded-lg border border-coral/40 bg-coral-light p-4 text-sm text-coral-dark">
						The scan returned an analysis we couldn&apos;t read, so nothing is
						listed below. Treat this as “unknown”, not “no issues”.
					</div>
				)}

				<section>
					<h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
						Recommendations ({recommendations.length})
					</h2>
					{recommendations.length === 0 ? (
						<div className="flex items-center gap-2 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
							<Inbox className="h-4 w-4" />
							Nothing outstanding in the latest scan.
						</div>
					) : (
						<ul className="space-y-2">
							{recommendations.map((item, i) => (
								<RecommendationRow key={`${item.title}-${i}`} item={item} />
							))}
						</ul>
					)}
				</section>

				{/* Competitors the analysis rates as beatable, with the reason. Only
				    shown when present — not every report contains them. */}
				{vulnerable.length > 0 && (
					<section data-testid="gbp-vulnerable">
						<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
							Vulnerable competitors ({vulnerable.length})
						</h2>
						<ul className="space-y-2">
							{vulnerable.map((item, i) => (
								<li
									key={`${item.name}-${i}`}
									className="flex items-start gap-3 rounded-lg border border-border bg-card p-4"
								>
									<Target className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
									<div className="min-w-0">
										<p className="font-medium text-foreground">{item.name}</p>
										{item.reason && (
											<p className="mt-1 text-sm text-muted-foreground">
												{item.reason}
											</p>
										)}
									</div>
								</li>
							))}
						</ul>
					</section>
				)}

				{/* Directories worth a listing. Some arrive without a usable link, so
				    the name has to stand on its own. */}
				{citations.length > 0 && (
					<section data-testid="gbp-citations">
						<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
							Relevant citations ({citations.length})
						</h2>
						<ul className="space-y-2">
							{citations.map((item, i) => (
								<li
									key={`${item.name}-${i}`}
									className="rounded-lg border border-border bg-card p-4"
								>
									{item.url ? (
										<a
											href={item.url}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center gap-2 text-sm font-medium text-coral hover:underline"
										>
											{item.name}
											<ExternalLink className="h-3.5 w-3.5" />
										</a>
									) : (
										<span className="text-sm font-medium text-foreground">
											{item.name}
										</span>
									)}
								</li>
							))}
						</ul>
					</section>
				)}

				<section>
					<h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
						Already complete ({completed.length})
					</h2>
					{completed.length === 0 ? (
						<div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
							Nothing recorded as complete yet.
						</div>
					) : (
						<ul className="space-y-2">
							{completed.map((item, i) => (
								<li
									key={`${item.title}-${i}`}
									className="flex items-start gap-3 rounded-lg border border-teal/30 bg-teal-light/40 p-4"
								>
									<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-teal-dark" />
									<div>
										<span className="text-sm font-medium text-foreground">
											{item.title}
										</span>
										{item.reason && (
											<p className="mt-1 text-sm text-muted-foreground">
												{item.reason}
											</p>
										)}
									</div>
								</li>
							))}
						</ul>
					)}
				</section>
				</TabsContent>

				<TabsContent value="audit">
					<GbpAuditPanel
						tenantId={tenantId}
						placeId={branch}
						runId={selectedAudit}
						onBackToLatest={backToLatestAudit}
						section={selectedSection}
						onSectionChange={setSection}
					/>
				</TabsContent>

				<TabsContent value="reviews">
					<GbpReviewsPanel
						tenantId={tenantId}
						placeId={branch}
						runId={selectedAudit}
					/>
				</TabsContent>

				<TabsContent value="history" className="space-y-8">
					<section>
						<h2 className="text-sm font-semibold mb-1">Visibility scans</h2>
						<GbpScanHistory
							tenantId={tenantId}
							placeId={branch}
							onSelectScan={viewScan}
						/>
					</section>
					<section>
						<h2 className="text-sm font-semibold mb-1">Profile audits</h2>
						<GbpAuditHistory
							tenantId={tenantId}
							placeId={branch}
							onSelectRun={viewAudit}
						/>
					</section>
				</TabsContent>
			</Tabs>
		</div>
	);
}
